import { Box, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import * as React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";

import CircularProgress from "@mui/material/CircularProgress";
import { Protocol } from "../../../model/device";
import { useTheme } from "@emotion/react";
import { Text, SubSubTitle } from "../../general/typography";
import { pathToPortMap } from "../selects/usb_port_select";
const isSuccessStep = (stat_code, step_code) => {
  if (stat_code === 500 || stat_code === step_code) return 0;
  else if (stat_code === 200 || stat_code === 202 || stat_code > step_code) return 1;
  else if (step_code > stat_code) return -1;
};

const ProbeResultLine = ({ step, result }) => {
  if (!step.name) return null;

  const state = isSuccessStep(result.status_code, step.code);

  const Icon = ({ state }) => {
    if (state === 0) return <ErrorIcon style={{ color: "#d32f2f" }} />;
    else if (state === 1) return <CheckCircleIcon style={{ color: "#388e3c" }} />;
    else if (state === -1) return <ErrorIcon style={{ color: "#d4d4d4" }} />;
  };

  const message = (state) => {
    if (state === 0) return result.info;
    else if (state === 1) return step.default_msg;
    else if (state === -1) return "Step not executed due to error in a previous step.";
  };

  const color = (state) => {
    if (state === 0) return "#d32f2f";
    else if (state === 1) return "#388e3c";
    else if (state === -1) return "#d4d4d4";
  };

  return (
    <ListItem key={step.code}>
      <ListItemIcon>
        <Icon state={state} />
      </ListItemIcon>
      <ListItemText
        primary={step.name}
        secondary={message(state)}
        primaryTypographyProps={{ style: { color: color(state) } }}
      />
    </ListItem>
  );
};

const ProbeResultList = ({ results, settings, fetching }) => {
  if (!results) return null;
  const probeSteps = [
    { name: "Device Connection", code: 404, default_msg: `Successfully connected to ${results.device?.address}` },
    results.device?.protocol === Protocol.RS485
      ? { name: null, code: null, default_msg: null }
      : {
          name: "Modbus port",
          code: 405,
          default_msg: `Successfully connected to port ${results.device?.port}`,
        },
    {
      name: "Modbus connection",
      code: 406,
      default_msg: `Successfully connected to ${results.device?.address} over Modbus`,
    },
    { name: "Modbus read", code: 407, default_msg: `Successfully read from slave ${results.device?.slave}` },
    {
      name: "Device verification",
      code: 408,
      default_msg: `Successfully verified ${results.device?.device_type} ${results.device?.device_class} ${results.device?.serial_number ? "with sn: " + results.device?.serial_number : ""}`,
    },
  ];

  return (
    <Box display="flex" flexDirection="column" pb={1} px={1} width="100%">
      <List>
        {probeSteps.map((step, index) => (
          <ProbeResultLine key={index} result={results} step={step} />
        ))}
      </List>
    </Box>
  );
};

const ResultsAddable = ({ results, settings }) => {
  const list = React.useMemo(
    () => results.filter((obj) => (obj.status_code === 200 || obj.status_code === 202) && !obj.existing_device),
    results,
  );
  if (list.length > 0) {
    return (
      <>
        <SubSubTitle>
          {list.length} device{list.length > 1 ? "s" : ""} ready to connect:
        </SubSubTitle>
        {list.map((obj, idx) => {
          return <ProbeResult key={idx} result={obj} settings={settings} />;
        })}
      </>
    );
  }
};

const ResultsExisting = ({ results, settings }) => {
  const list = React.useMemo(() => results.filter((obj) => obj.existing_device), results);
  if (list.length > 0) {
    return (
      <>
        <SubSubTitle>
          {list.length} existing device{list.length > 1 ? "s" : ""} found:
        </SubSubTitle>
        {list.map((obj, idx) => {
          return <ProbeResult key={idx} result={obj} settings={settings} />;
        })}
      </>
    );
  }
};

const ResultsFailed = ({ results, settings }) => {
  const list = React.useMemo(
    () => results.filter((obj) => obj.status_code !== 200 && obj.status_code !== 202),
    results,
  );
  if (list.length > 0) {
    return (
      <>
        <SubSubTitle>
          {list.length} failure{list.length > 1 ? "s" : ""}:
        </SubSubTitle>
        {list.map((obj, idx) => {
          return <ProbeResult key={idx} result={obj} settings={settings} />;
        })}
      </>
    );
  }
};

const ProbeResult = ({ result, settings }) => {
  const [open, setOpen] = React.useState(false);
  const clickable = result.status_code < 500;
  const msg =
    settings.protocol === Protocol.RS485 ? `[${pathToPortMap[result.device?.address]}] ${result.info}` : result.info;
  return (
    <>
      <ProbeStatusBar onClick={() => setOpen((prev) => !prev)} status={result} clickable={clickable} info={msg} />
      {open && <ProbeResultList results={result} settings={settings} />}
    </>
  );
};

export const ProbeResults = ({ results, settings, fetching }) => {
  if (fetching) {
    return <ProbePendingBar />;
  } else if (!results) {
    return <ProbeStatusBar status={{ status_code: 500, info: "No or invalid response from Elion box, retry probe" }} />;
  }
  return (
    <>
      <ResultsAddable results={results} settings={settings} />
      <ResultsExisting results={results} settings={settings} />
      <ResultsFailed results={results} settings={settings} />
      {/* {results.map((obj, idx) => {return <ProbeResult key={idx} result={obj} settings={settings}/>})} */}
    </>
  );
};

export const ProbeStatusBar = ({ onClick, status, clickable = true, info = null }) => {
  const isSuccess = status.status_code === 200;
  const isWarning = status.status_code === 202;
  const msg = info ? info : status.info;
  const background = isSuccess
    ? status.existing_device
      ? "#efefef"
      : "#e8f5e9"
    : isWarning
      ? status.existing_device
        ? "#efefef"
        : "#fffdeb"
      : "#ffebee"; // white : yellow : green : red
  const textColor = isSuccess
    ? status.existing_device
      ? "#717171"
      : "#388e3c"
    : isWarning
      ? status.existing_device
        ? "#717171"
        : "#ebcb00"
      : "#d32f2f"; // grey : yellow : green : red
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      onClick={(e) => {
        if (clickable) {
          onClick(status);
        }
      }}
      mb={1}
      sx={{
        "&:hover": {
          cursor: clickable && "pointer",
        },
      }}
    >
      <Box
        width="100%"
        style={{
          padding: 15,
          textAlign: "center",
          backgroundColor: background,
          color: textColor,
        }}
      >
        <Stack alignItems="center" direction="row">
          {isSuccess ? (
            <CheckCircleIcon style={{ fontSize: 30, color: textColor }} />
          ) : isWarning ? (
            <WarningIcon style={{ fontSize: 30, color: textColor }} />
          ) : (
            <ErrorIcon style={{ fontSize: 30, color: textColor }} />
          )}
          <Typography marginLeft={1} variant="h6">
            {status.status_code}: {msg}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export const ProbePendingBar = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" width="100%">
      <Box
        marginTop={5}
        width="100%"
        style={{
          padding: "20px",
          textAlign: "center",
          backgroundColor: "#fce092",
          color: "#ffbe0a",
        }}
      >
        <Stack alignItems="center" direction="row">
          <CircularProgress />
          <Typography marginLeft={1} variant="h6">
            Probing...
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};
